<script setup lang="ts">
import { computed } from "vue";

import { getAlmType } from "@/backend/read/BackendReader";
import type { LinkedToItem } from "@/store/issueSelector";
import { useIssueSelectorStore } from "@/store/issueSelector";
import { useLinkStore } from "@/store/link";

import { highlightLinkedCards } from "../utils";
import IssueAdvancedFilter from "./IssueAdvancedFilter.vue";
import IssueAssigneeFilter from "./IssueAssigneeFilter.vue";
import IssueLinkedToFilter from "./IssueLinkedToFilter.vue";
import IssueTextFilter from "./IssueTextFilter.vue";
import IssueTypeFilter from "./IssueTypeFilter.vue";

const store = useIssueSelectorStore();

const handleTextFilterChange = (text: string) => {
  store.setText(text);
  void store.fetchIssues();
};

const handleTypeFilterChange = (typeIds: string[]) => {
  store.setSelectedTypeIds(typeIds);
  void store.fetchIssues();
};

const handleAssigneeFilterChange = (assigneeIds: string[]) => {
  store.setSelectedAssigneeIds(assigneeIds);
  void store.fetchIssues();
};

const almTool = computed(() => getAlmType());

const handleLinkedToFilterChange = (items: LinkedToItem[]) => {
  store.setSelectedLinkedToItems(items);
  void store.fetchIssues();

  // Update board highlighting based on filter state
  if (items.length === 0) {
    useLinkStore().removeAllMarks();
  } else {
    const cardIds = items.map((item) => item.id).filter((id): id is string => !!id);
    void highlightLinkedCards(cardIds);
  }
};
</script>

<template>
  <div class="issue-filter-bar">
    <IssueTextFilter :text="store.text" @change="handleTextFilterChange" />
    <IssueTypeFilter :selected="store.selectedTypeIds" @change="handleTypeFilterChange" />
    <IssueAssigneeFilter
      :selected="store.selectedAssigneeIds"
      @change="handleAssigneeFilterChange"
    />
    <IssueLinkedToFilter
      :selected="store.selectedLinkedToItems"
      @change="handleLinkedToFilterChange"
    />
    <IssueAdvancedFilter v-if="almTool === 'jira'" />
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.issue-filter-bar {
  display: flex;
  gap: 8px;
  padding: 4px 16px 12px;
  background-color: colors.$background-ui;
}
</style>
